// Export ("Mileage Log"): pick a period, vehicle and categories, check the CSV preview and totals,
// then download the CSV or open the printable report.

import { useState } from "react";
import { CategoryBadge, tintStyle } from "../components/Category.tsx";
import { FilterSelect, Notice, type Option, Segmented } from "../components/Controls.tsx";
import { Icon } from "../components/Icon.tsx";
import { useAccountData } from "../data/AccountData.tsx";
import { currencyOf } from "../data/derived.ts";
import { csvHeader, csvDate, csvMoney, csvRate, csvTime } from "../domain/csv.ts";
import { formatDistance, formatMoney, listText, plural, unitSymbol } from "../domain/format.ts";
import { metersPerUnit, rateAmount } from "../domain/rates.ts";
import { navigate } from "../lib/router.ts";
import { CustomDates, type MileagePeriodChoice, mileagePeriodOptions, useMileagePeriod } from "./Mileage.tsx";
import { type LogChoice, buildReport, choiceFromQuery, downloadCSV, openReport } from "./exportActions.ts";

const previewRows = 5;

export function Export() {
  const data = useAccountData();
  const initial = choiceFromQuery(new URLSearchParams(window.location.search), data);
  const choice = useMileagePeriod((initial.periodKind as MileagePeriodChoice) ?? "lastMonth");
  const [vehicleId, setVehicleId] = useState(initial.vehicleId);
  const [included, setIncluded] = useState<string[]>(initial.categories);

  const logChoice: LogChoice = { periodKind: choice.kind, from: choice.from, to: choice.to, vehicleId, categories: included };
  const report = buildReport(data, logChoice, choice.now);
  const currency = report.currencyCode ?? currencyOf(data);
  const unclassified = data.trips.filter((trip) =>
    !data.catalog.resolve(trip.categoryRaw)
    && trip.start >= report.interval.start && trip.start < report.interval.end
    && (vehicleId === "all" || trip.vehicleId?.toUpperCase() === vehicleId.toUpperCase())).length;
  const vehicleOptions: Option<string>[] = [{ value: "all", label: "All vehicles" }, ...data.vehicles.map((vehicle) => ({ value: vehicle.id, label: vehicle.name }))];
  const includedNames = data.catalog.all.filter((category) => included.includes(category.rawValue)).map((category) => category.name);
  const meters = report.totalDistance * metersPerUnit[report.unit];

  function toggle(raw: string) {
    setIncluded((current) => (current.includes(raw) ? current.filter((value) => value !== raw) : [...current, raw]));
  }

  return (
    <>
      <div className="cv-head">
        <div>
          <h1 className="cv-title">Mileage Log</h1>
          <p className="cv-sub">Download it as a spreadsheet, or open a report to print or save as PDF.</p>
        </div>
      </div>

      <div className="cv-split">
        <div className="cv-list">
          <section className="cv-card cv-card-pad" aria-labelledby="period-label">
            <div id="period-label" className="cv-label">Period</div>
            <Segmented label="Period" value={choice.kind} options={mileagePeriodOptions} onChange={choice.setKind} />
            {choice.kind === "custom" && <div style={{ marginTop: 10 }}><CustomDates from={choice.from} to={choice.to} setFrom={choice.setFrom} setTo={choice.setTo} /></div>}
            <p className="cv-hint" style={{ marginTop: 10 }}>{report.periodTitle} · {choice.subtitle.split(" · ").at(-1)}</p>

            {data.vehicles.length > 1 && (
              <div style={{ marginTop: 22 }}>
                <div className="cv-label">Vehicle</div>
                <FilterSelect label="Vehicle" hideLabel value={vehicleId} options={vehicleOptions} onChange={setVehicleId} />
              </div>
            )}

            <div style={{ marginTop: 22 }}>
              <div id="include-label" className="cv-label">Include</div>
              <div className="cv-cats" role="group" aria-labelledby="include-label">
                {data.catalog.choices().map((category) => {
                  const on = included.includes(category.rawValue);
                  return (
                    <button key={category.rawValue} className="cv-toggle-chip" type="button" aria-pressed={on} style={tintStyle(category)} onClick={() => toggle(category.rawValue)}>
                      <span className="box">{on && <Icon name="check" size="sm" />}</span>{category.name}
                    </button>
                  );
                })}
              </div>
              <p className="cv-hint" style={{ marginTop: 10 }}>Each trip lists its date, times, from and to, purpose, notes, vehicle, distance, rate and value, with totals by category.</p>
            </div>
          </section>

          {unclassified > 0 && (
            <Notice action={<button className="cv-btn cv-btn-secondary" type="button" onClick={() => navigate("/trips?review=1")}>Classify</button>}>
              <b>{plural(unclassified, "trip")} in this period {unclassified === 1 ? "isn’t" : "aren’t"} classified</b>
              <div className="cv-hint">They’re left out of this log until they have a category.</div>
            </Notice>
          )}

          <section className="cv-card" aria-labelledby="csv-title" style={{ overflow: "hidden" }}>
            <div className="cv-card-pad" style={{ display: "flex", alignItems: "baseline", gap: 12, paddingBottom: 12, flexWrap: "wrap" }}>
              <h2 id="csv-title" className="cv-section-title" style={{ fontSize: 17 }}>CSV preview</h2>
              <span className="cv-hint">
                {report.rows.length === 0 ? "No trips in this log yet" : `First ${Math.min(previewRows, report.rows.length)} of ${plural(report.rows.length, "row")} · same columns as the app’s export`}
              </span>
            </div>
            <div className="cv-table-box">
              <table className="cv-table cv-csv">
                <thead>
                  <tr>{csvHeader.map((column) => <th key={column} scope="col" className={["Distance", "Rate", "Value"].includes(column) ? "r" : undefined}>{column}</th>)}</tr>
                </thead>
                <tbody>
                  {report.rows.slice(0, previewRows).map((row) => (
                    <tr key={row.tripId}>
                      <td>{csvDate(row.start)}</td>
                      <td>{csvTime(row.start)}</td>
                      <td>{row.end ? csvTime(row.end) : ""}</td>
                      <td>{row.from ?? ""}</td>
                      <td>{row.to ?? ""}</td>
                      <td>{row.purpose ?? ""}</td>
                      <td>{row.category.name}</td>
                      <td>{row.vehicleName ?? ""}</td>
                      <td className="r">{row.distance.toFixed(1)}</td>
                      <td>{unitSymbol[report.unit]}</td>
                      <td className="r">{row.rate ? csvRate(rateAmount(row.rate)) : ""}</td>
                      <td className="r">{row.value !== undefined ? csvMoney(row.value) : ""}</td>
                      <td>{row.rate?.currencyCode ?? ""}</td>
                      <td>{row.isRoundTrip ? "Yes" : "No"}</td>
                      <td>{row.notes ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <aside className="cv-card cv-card-pad" aria-labelledby="sum-title" style={{ flex: "1 1 340px", maxWidth: 420, position: "sticky", top: 16 }}>
          <h2 id="sum-title" className="cv-section-title" style={{ fontSize: 17 }}>{report.periodTitle}</h2>
          <p className="cv-hint" style={{ marginTop: 2 }}>{report.vehicleName ?? "All vehicles"} · {includedNames.length > 0 ? listText(includedNames) : "No categories"}</p>
          <div className="cv-hero-val" style={{ fontSize: 40, marginTop: 14 }}>{report.totalValue !== undefined ? formatMoney(report.totalValue, currency) : "—"}</div>
          <p className="cv-hint num" style={{ marginTop: 2 }}>{formatDistance(meters, report.unit)} · {plural(report.rows.length, "trip")}</p>
          {report.categoryTotals.length > 0 && (
            <table className="cv-table" style={{ marginTop: 14, fontSize: 13.5 }}>
              <caption className="cv-visually-hidden">Totals by category</caption>
              <tbody>
                {report.categoryTotals.map((total) => (
                  <tr key={total.category.rawValue}>
                    <th scope="row" style={{ padding: "9px 0", fontWeight: 400, textAlign: "left" }}><span className="cv-cat-cell"><CategoryBadge category={total.category} small />{total.category.name}</span></th>
                    <td className="r" style={{ padding: "9px 0" }}>{formatDistance(total.distance * metersPerUnit[report.unit], report.unit)}</td>
                    <td className="r" style={{ padding: "9px 0", fontWeight: 600 }}>{total.value !== undefined ? formatMoney(total.value, currency) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 18 }}>
            <button className="cv-btn cv-btn-primary cv-btn-lg" type="button" onClick={() => downloadCSV(report)} disabled={included.length === 0}>
              <Icon name="download" />Download CSV
            </button>
            <button className="cv-btn cv-btn-secondary cv-btn-lg" type="button" onClick={() => openReport(logChoice)} disabled={included.length === 0}>
              <Icon name="print" />Open Printable Report
            </button>
          </div>
          <p className="cv-hint" style={{ marginTop: 12 }}>The report opens in a new tab. Use your browser’s Print to print it or save it as a PDF.</p>
        </aside>
      </div>
    </>
  );
}
